// ===========================================================================
// reportedProduction.js - THE rule for "what did this farm report to RMA?"
//
// Extracted from the inline useEffect in GrainMarketingView so the Claims
// Manager can ask the same question and get the same answer. Two callers, one
// engine - the copRules.js / grainTargets.js / miYieldModel.js pattern. A second
// copy of this rule would not be a wrong formula, it would be a DIFFERENT
// formula, and the two would disagree on a claim.
//
// PURE: takes rows, returns an answer. The fetch stays with each caller.
//
// -- THE TRAP THIS ENCODES --------------------------------------------------
// A production year appears in the APH database of EVERY policy year that uses
// it, so the identical line sits under -26 and -27. 1,171 cells repeat book-wide
// and a naive sum double-counts 174 of 854 farm-crop-years - Schofield's 2025
// corn reads 505,396 bu that way against 128,372 real.
//
// The rule is NEWEST policy_number wins, then sum everything it carries. Not a
// dedupe heuristic: repeats INSIDE one policy are REAL (248 cells hold two rows,
// because a county can hold several APH databases, one per FSN/yield line) and a
// (county, type, practice) dedupe would silently drop a genuine second yield
// line. 21 cells disagree across policies by up to 520,540 bu, so picking any
// other policy would be a guess.
//
// Filters that matter, each for its own reason:
//   record_type in ('A','PA')  - actual only. Transitional and Z are not
//                                reported production.
//   excluded_from_avg !== true - the grower excluded it from the APH.
//   unit_of_measure === 'BU'   - `unit_of_measure` says BU on dry-bean rows that
//                                are plainly pounds (2,433 "bu"/ac) and TON on
//                                corn silage. This feeds BUSHEL surfaces, so a
//                                crop is only offered when its rows say BU.
//
// AND WHAT IT IS NOT: this is the AIP's own production report. It is NOT
// delivered bushels. Kevin, 2026-08-16: "not everyone delivers bushels to
// howletts or delivers all at harvest." Sullivan's 2026 wheat reports 36,200 bu
// against 34,204 delivered to Howlett - the ~2,000 difference is real grain one
// elevator's ledger cannot see. Never present one as a check on the other
// without saying which is which.
// ===========================================================================

const ACTUAL_RECORD_TYPES = ['A', 'PA'];

// Wheat is reported under two names. Everything else matches on its own name.
function cropMatchesReported(rowCrop, wantCrop) {
  const c = String(rowCrop || '').toUpperCase();
  const want = String(wantCrop || '').toUpperCase();
  if (want === 'WHEAT') return c === 'WHEAT' || c === 'WINTER WHEAT';
  return c === want;
}

// Rows this crop can legitimately contribute. Kept separate so a caller can show
// WHY a crop resolved to nothing, rather than just an empty answer.
function eligibleRows(rows, crop) {
  return (rows || []).filter((r) =>
    cropMatchesReported(r.crop, crop)
    && ACTUAL_RECORD_TYPES.indexOf(String(r.record_type || '').toUpperCase()) !== -1
    && String(r.unit_of_measure || '').toUpperCase() === 'BU'
    && r.production != null
    && r.excluded_from_avg !== true
    && r.policy_number
  );
}

// One crop's reported production, or null when nothing qualifies.
// Returns { bu, acres, lines, policy, policiesSeen } - policiesSeen names how
// many policies held rows, because >1 is exactly the double-count trap and a
// caller may want to say so.
function reportedProductionForCrop(rows, crop) {
  const mine = eligibleRows(rows, crop);
  if (!mine.length) return null;
  const policies = Array.from(new Set(mine.map((r) => r.policy_number))).sort();
  const newest = policies[policies.length - 1];
  const use = mine.filter((r) => r.policy_number === newest);
  const bu = use.reduce((s, r) => s + (Number(r.production) || 0), 0);
  const ac = use.reduce((s, r) => s + (Number(r.acres) || 0), 0);
  if (!(bu > 0)) return null;
  return { bu, acres: ac || null, lines: use.length, policy: newest, policiesSeen: policies.length };
}

// crop -> answer, for a list of crops. Crops with nothing reportable are absent
// rather than zero: "not reported" and "reported zero" are different facts.
function reportedProductionByCrop(rows, crops) {
  const out = {};
  const list = crops || [];
  for (let i = 0; i < list.length; i++) {
    const r = reportedProductionForCrop(rows, list[i]);
    if (r) out[list[i]] = r;
  }
  return out;
}

// The columns every caller needs. Kept here so two fetches cannot drift into
// asking for different fields and quietly filtering on a column one of them
// never selected.
const REPORTED_PRODUCTION_SELECT =
  'crop,crop_year,county,type,practice,record_type,unit_of_measure,acres,production,policy_number,excluded_from_avg';

module.exports = {
  ACTUAL_RECORD_TYPES,
  REPORTED_PRODUCTION_SELECT,
  cropMatchesReported,
  eligibleRows,
  reportedProductionForCrop,
  reportedProductionByCrop,
};
